import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";

type TargetType = {
  coordinates: { x: number; y: number }[];
  gif: string;
  size: number;
  blinkAndResizing: boolean;
};

type Props = {
  backgroundColor: string;
  caseSize: number;
  // enableBlinkingAndResizing: boolean;
  isPausedAndNotFullScreenRef: React.MutableRefObject<boolean>;
  targetColor: string[];
  // Control the animation type applied when target.blinkAndResizing is true
  movementMode?: "blink" | "spin" | "both"; // default 'both' to preserve previous behavior
  targetPositions: {
    sameShape: boolean;
    targets: {
      position: string;
      size: number;
      gif: string;
      blinkAndResizing: boolean;
    }[];
  }[];
  answer: React.MutableRefObject<boolean | null>;
};

const ModuleFeedbackTargetsV2 = forwardRef(
  (
    {
      // enableBlinkingAndResizing,
      targetPositions,
      caseSize,
      isPausedAndNotFullScreenRef,
      backgroundColor,
      answer,
      movementMode = "both",
    }: Props,
    ref
  ) => {
    const [currentTargets, setCurrentTargets] = useState<TargetType[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
    const [canShowTarget, setCanShowTarget] = useState(true);
    const [quadrillageVisible, setQuadrillageVisible] = useState(false);
    // Animation state not needed now; animations done via CSS classes
    // const canvasWidth = 16 * caseSize;
    // const canvasHeight = 12 * caseSize;

    const cellWidth = (5.5 / 4) * caseSize;
    const cellHeight = caseSize;

    const convertGridPositionToCoordinates = useCallback((
      position: string,
      columnsToRemove: number,
      rowsToRemove: number,
      cellWidth: number,
      cellHeight: number
    ) => {
      const letter = position.charAt(0).toUpperCase();
      const number = parseInt(position.slice(1), 10);
      const col = letter.charCodeAt(0) - 65;
      const row = number - 1;
      const x = col * cellWidth;
      const y = row * cellHeight;

      // Ajuster la position pour les colonnes et lignes supprimées
      const { x: adjustedX, y: adjustedY } =
        adjustPositionForRemovedColumnsAndRows(
          x,
          y,
          columnsToRemove,
          rowsToRemove,
          cellWidth,
          cellHeight
        );
      return { x: adjustedX, y: adjustedY };
  }, []);
    // key handling effect moved below showNewTarget declaration for stable reference

    const calculateCanvasDimensions = useCallback(() => {
      // Taille maximale de l'écran
      const maxCanvasWidth = window.innerWidth;
      const maxCanvasHeight = window.innerHeight;

      // Dimensions de base
      let maxColumns = 16; // 16 colonnes de base
      let maxRows = 12; // 12 lignes de base

      let canvasWidth = maxColumns * cellWidth;
      let canvasHeight = maxRows * cellHeight;

      // Si le canvas dépasse la taille de l'écran, réduire en supprimant symétriquement
      let columnsToRemove = 0;
      if (canvasWidth > maxCanvasWidth) {
        const excessColumns = Math.ceil(
          (canvasWidth - maxCanvasWidth) / cellWidth
        );
        columnsToRemove = Math.ceil(excessColumns / 2); // Supprimer de chaque côté
        maxColumns -= 2 * columnsToRemove; // Supprimer symétriquement à gauche et à droite
        canvasWidth = maxColumns * cellWidth;
      }

      let rowsToRemove = 0;
      if (canvasHeight > maxCanvasHeight) {
        const excessRows = Math.ceil(
          (canvasHeight - maxCanvasHeight) / cellHeight
        );
        rowsToRemove = Math.ceil(excessRows / 2); // Supprimer de chaque côté
        maxRows -= 2 * rowsToRemove; // Supprimer symétriquement en haut et en bas
        canvasHeight = maxRows * cellHeight;
      }

      return {
        canvasWidth,
        canvasHeight,
        maxColumns,
        maxRows,
        columnsToRemove,
        rowsToRemove,
      };
    }, [cellHeight, cellWidth]);

    useEffect(() => {
      console.log("currentTargets changed:", targetPositions);
      const { canvasWidth, canvasHeight } = calculateCanvasDimensions();

      const canvas = canvasRef.current;
      if (!canvas) return;

      const context = canvas.getContext("2d");
      if (!context) return;

      context.fillStyle = backgroundColor;
      context.fillRect(0, 0, canvasWidth, canvasHeight);


      if (quadrillageVisible) {
      // Couleur des lignes du quadrillage
      if (backgroundColor === "white") {
        context.strokeStyle = "black";
      } else {
      context.strokeStyle = "white";
      }
      context.lineWidth = 1;

      // Dessiner les lignes verticales
      for (let x = 0; x <= canvasWidth; x += cellWidth) {
        context.beginPath();
        context.moveTo(x, 0); // Commencer en haut
        context.lineTo(x, canvasHeight); // Descendre jusqu'en bas
        context.stroke();
      }

      // Dessiner les lignes horizontales
      for (let y = 0; y <= canvasHeight; y += cellHeight) {
        context.beginPath();
        context.moveTo(0, y); // Commencer à gauche
        context.lineTo(canvasWidth, y); // Aller jusqu'à droite
        context.stroke();
      }
    }

      // Draw the cross in the center
      const centerX = canvasWidth / 2;
      const centerY = canvasHeight / 2;
      const crossLength = (cellHeight / 4) * 0.7;

      context.beginPath();
      context.strokeStyle = backgroundColor === "white" ? "black" : "white";
      context.lineWidth = 2;
      context.moveTo(centerX - crossLength / 2, centerY);
      context.lineTo(centerX + crossLength / 2, centerY);
      context.stroke();

      context.beginPath();
      context.moveTo(centerX, centerY - crossLength / 2);
      context.lineTo(centerX, centerY + crossLength / 2);
      context.stroke();
  }, [currentTargets, backgroundColor, cellHeight, cellWidth, calculateCanvasDimensions, quadrillageVisible, targetPositions]);

    const adjustPositionForRemovedColumnsAndRows = (
      x: number,
      y: number,
      columnsToRemove: number,
      rowsToRemove: number,
      cellWidth: number,
      cellHeight: number
    ) => {
      const adjustedX = x - columnsToRemove * cellWidth;
      const adjustedY = y - rowsToRemove * cellHeight;
      return { x: adjustedX, y: adjustedY };
    };

    const isPositionInsideCanvas = (
      x: number,
      y: number,
      sizeX: number, // Ajout de la taille
      sizeY: number, // Ajout de la taille
      canvasWidth: number,
      canvasHeight: number
    ) => {
      return (
        x >= 0 &&
        x + sizeX <= canvasWidth && // S'assurer que la taille ne dépasse pas à droite
        y >= 0 &&
        y + sizeY <= canvasHeight // S'assurer que la taille ne dépasse pas en bas
      );
    };

    type Target = {
      position: string;
      size: number;
    };

    type Salvo = {
      sameShape: boolean;
      targets: Target[];
    };

    const filterTargetsWithinCanvas = useCallback((
      salvo: Salvo,
      canvasWidth: number,
      canvasHeight: number
    ) => {
      const { columnsToRemove, rowsToRemove } = calculateCanvasDimensions();
      const validTargets = salvo.targets.filter(
        (target: { position: string; size: number }) => {
          const { x, y } = convertGridPositionToCoordinates(
            target.position,
            columnsToRemove,
            rowsToRemove,
            cellWidth,
            cellHeight
          );
          const sizeInPixelsX = target.size * cellWidth;
          const sizeInPixelsY = target.size * cellHeight;
          return isPositionInsideCanvas(
            x,
            y,
            sizeInPixelsX,
            sizeInPixelsY,
            canvasWidth,
            canvasHeight
          ); // Ajout de la taille
        }
      );

      return validTargets.length > 0; // On conserve seulement les salves avec des cibles valides
    }, [calculateCanvasDimensions, cellWidth, cellHeight, convertGridPositionToCoordinates]);

    const showNewTarget = useCallback(() => {
      if (canShowTarget && !isPausedAndNotFullScreenRef.current) {
        setCanShowTarget(false);

        const delay = 500;
        setTimeout(() => {
          const { canvasWidth, canvasHeight, columnsToRemove, rowsToRemove } =
            calculateCanvasDimensions();

          // Filtrer les salves qui ont au moins une cible dans le canvas
          const validTargetPositions = targetPositions.filter((salvo) =>
            filterTargetsWithinCanvas(salvo, canvasWidth, canvasHeight)
          );

          if (validTargetPositions.length === 0) {
            setCanShowTarget(true); // Si aucune salve valide, permettre de relancer le processus
            return;
          }

          const salvo =
            validTargetPositions[
              Math.floor(Math.random() * validTargetPositions.length)
            ];
          const targetCount = 2;

          answer.current = salvo.sameShape;

          const shuffledTargets = salvo.targets
            .sort(() => Math.random() - 0.5)
            .slice(0, targetCount);

          const newTargets = shuffledTargets.map((target) => ({
            coordinates: [
              convertGridPositionToCoordinates(
                target.position,
                columnsToRemove,
                rowsToRemove,
                cellWidth,
                cellHeight
              ),
            ],
            gif: target.gif,
            size: target.size,
            blinkAndResizing: target.blinkAndResizing,
          }));

          setCurrentTargets(newTargets);
          setCanShowTarget(true);
        }, delay);
      }
  }, [canShowTarget, isPausedAndNotFullScreenRef, calculateCanvasDimensions, targetPositions, answer, cellHeight, cellWidth, convertGridPositionToCoordinates, filterTargetsWithinCanvas]);

    // Key handling effect (after showNewTarget is declared)
    useEffect(() => {
      const handleKeyPress = (e: KeyboardEvent) => {
        if (e.key === " ") {
          if (currentTargets.length > 0 && !isPausedAndNotFullScreenRef.current) {
            setCurrentTargets([]);
            showNewTarget();
          }
        } else if (e.key === "s") {
          showNewTarget();
        }

        if (e.key === "q" || e.key === "Q") {
          setQuadrillageVisible((prev) => !prev);
        }
      };

      window.addEventListener("keydown", handleKeyPress);
      return () => window.removeEventListener("keydown", handleKeyPress);
    }, [currentTargets, isPausedAndNotFullScreenRef, showNewTarget]);

    useImperativeHandle(ref, () => ({
      triggerShowNewTarget: () => {
        // console.log("triggerShowNewTarget called");
        showNewTarget();
      },
    }));

    const { canvasWidth, canvasHeight } = calculateCanvasDimensions();
    // console.log("currentTargets", currentTargets);
    // console.log("canvasWidth", canvasWidth);
    // console.log("canvasHeight", canvasHeight);
    // console.log("caseSize", caseSize);
    return (
      <div
        style={{
          position: "relative",
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "black", // backgroundColor prop passed here
        }}
      >
        <div
          style={{
            position: "relative",
            height: canvasHeight,
            width: canvasWidth,
          }}
        >
          <canvas
            ref={canvasRef}
            width={canvasWidth}
            height={canvasHeight}
            style={{ position: "absolute", top: 0, left: 0 }}
          />
          {currentTargets.map((target, index) => {
            const firstCoord = target.coordinates[0];

            const size = (caseSize * target.size) / 2;

            const blockWidth = cellWidth * target.size;
            const blockHeight = cellHeight * target.size;
            // Taille constante de la cible (on reprend l'échelle utilisée auparavant pour size=1 : (caseSize)/2 )
            // Centre du bloc multi-cellules
            const centerX = firstCoord.x + blockWidth / 2;
            const centerY = firstCoord.y + blockHeight / 2;

            // Adjust the position to center the image
            const x = centerX - size / 2;
            const y = centerY - size / 2;

            // Choose animation classes based on movementMode
            const animClass = target.blinkAndResizing
              ? movementMode === "blink"
                ? "animate-pulse"
                : movementMode === "spin"
                ? "animate-spin"
                : "animate-pulse animate-spin"
              : "";

            return (
              <img
                key={index}
                src={target.gif}
                className={animClass}
                style={{
                  animationDuration: target.blinkAndResizing ? "2s" : "",
                  position: "absolute",
                  top: y,
                  left: x,
                  width: size,
                  height: size,
                  // keep always visible; pulse/opacity handles blinking effect visually
                  visibility: "visible",
                }}
                alt="target"
              />
            );
          })}
        </div>
      </div>
    );
  }
);

export default ModuleFeedbackTargetsV2;
